import { type LocationWithRegion } from '#/types/Location';

/**
 * Creates an OTP2-style location string based on the location name, lat, lon and region.
 * Format: name::lat,lon::region or ::lat,lon::region if no name
 * This overloads the otp2 fromPlace/toPlace String format to include the region if it exists.
 * The region is optional and can be omitted.
 * If the location name is not provided, it will be an empty string so that the format
 * remains consistent and parsable.
 * The lat and lon are required and must be valid numbers.
 */
export function getOtp2LocationString(location: LocationWithRegion): string {
  const latLonString = `${location.lat},${location.lon}`;
  const { name, region } = location;

  // Base string with empty name if none provided
  const baseString = `${name ?? ''}::${latLonString}`;

  // Append region if it exists
  return region ? `${baseString}::${region}` : baseString;
}

/**
 * Parses an OTP2-style location string and converts it back to a LocationWithRegion object.
 * Format can be:
 * - "name::lat,lon::region"
 * - "::lat,lon::region"
 * - "name::lat,lon"
 * - "::lat,lon"
 * @throws Error if the location string format is invalid
 */
export function parseOtp2LocationString(otpString: string): LocationWithRegion {
  const parts = otpString.split('::');

  // Handle different format cases
  let name: string | undefined;
  let coords: string;
  let region: string | undefined;

  switch (parts.length) {
    case 3: // name::lat,lon::region or ::lat,lon::region
      [name, coords, region] = parts;
      break;
    case 2: // name::lat,lon or ::lat,lon
      [name, coords] = parts;
      break;
    default:
      throw new Error('Invalid OTP location string format');
  }

  // Parse coordinates
  const [lat, lon] = coords.split(',').map(Number);

  if (Number.isNaN(lat) || Number.isNaN(lon)) {
    throw new Error('Invalid coordinates in OTP location string');
  }

  // Build location object, only including non-empty properties
  const location: LocationWithRegion = { lat, lon };
  if (name) location.name = name;
  if (region) location.region = region;

  return location;
}

/**
 * Non-throwing variant of {@link parseOtp2LocationString}. Returns the parsed
 * location, or `undefined` when the string is malformed (e.g. a hand-edited or
 * truncated shared link with bad coordinates). Use this where a bad value
 * should be handled gracefully rather than crashing the app.
 */
export function tryParseOtp2LocationString(
  otpString: string
): LocationWithRegion | undefined {
  try {
    return parseOtp2LocationString(otpString);
  } catch {
    return undefined;
  }
}
